import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useFeatureEntitlements } from '@frontegg/react';
import { Badge, Card, Divider, Tooltip } from 'antd';
import {
  CalendarOutlined,
  FilterOutlined,
  HistoryOutlined,
  PlusOutlined,
  UnorderedListOutlined,
  UploadOutlined,
  ScheduleOutlined,
} from '@ant-design/icons';

import {
  Table,
  Button,
  SkeletonLoader,
  Segmented,
} from '@insight-health-ai/roseate/components';
import {
  PatientDropdown,
  PractitionerDropdown,
  DateRangePicker,
  AppointmentStatusDropdown,
  AppointmentSavedViewDropdown,
  AppointmentSavedView,
} from 'src/components';
import { SorterResult } from 'antd/es/table/interface';
import {
  useGetAppointmentInfiniteList,
  useGetAppointmentTypes,
} from 'src/hooks/appointment';
import styled from '@emotion/styled';
import { usePageHeader, usePersistedFilters } from 'src/hooks/misc';
import {
  appointmentDefaultFilters,
  appointmentDefaultSort,
  appointmentDefaultView,
  AppointmentFilters,
  AppointmentSort,
  extractFiltersFromRecentSearch,
  getAppointmentListColumns,
  sortDirectionMap,
  transformCurrentFiltersToSavedView,
  transformSavedFiltersToApiParams,
} from './helpers';
import { Appointment } from 'src/models';
import dayjs from 'dayjs';
import { AmbientAi } from '@insight-health-ai/roseate/icons';
import { useScreenSize } from '@insight-health-ai/roseate/utils';
import {
  CreateAppointmentModal,
  CreateAppointmentModalProps,
} from './createAppointment';
import { AppointmentListCalendarView } from './calendarView';
import { analytics, RUM } from 'src/services';
import { trackOpenedCareConnectSummary } from './helpers';
import { AppointmentBulkUploadDrawer } from './bulkUpload';
import { AppointmentBulkUploadHistoryDrawer } from './bulkUpload/appointmentBulkUploadHistory';
import { AppointmentMetrics } from './metrics';

const StyledCard = styled(Card)`
  height: 100%;
  display: flex;
  flex-direction: column;

  .ant-card-head {
    flex-shrink: 0;
  }

  .ant-card-body {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
`;

export const AppointmentList = () => {
  const { user } = useAuth();
  const { mediaMax } = useScreenSize();
  const isMobile = mediaMax('md');
  const navigate = useNavigate();
  const [viewType, setViewType] = useState<'list' | 'calendar'>('list');
  const [createAppointmentModalOpen, setCreateAppointmentModalOpen] =
    useState<null | {
      initialValues: CreateAppointmentModalProps['initialValues'];
      editAppointmentUuid?: Appointment['appt_uuid'];
    }>(null);
  const [bulkUploadModalOpen, setBulkUploadModalOpen] = useState(false);
  const [bulkUploadHistoryModalOpen, setBulkUploadHistoryModalOpen] =
    useState(false);
  const [defaultSavedView, setDefaultSavedView] =
    useState<AppointmentSavedView | null>(appointmentDefaultView);
  const [selectedSavedView, setSelectedSavedView] =
    useState<AppointmentSavedView | null>();
  const { isEntitled: hideDashboardWidgetAccess } = useFeatureEntitlements(
    'hide-dashboard-widget'
  );

  const savedView = useMemo(() => {
    return defaultSavedView
      ? extractFiltersFromRecentSearch(defaultSavedView)
      : undefined;
  }, [defaultSavedView]);

  const {
    filters: persistedFilters,
    sort,
    updateFilters,
    setFilters,
    updateSort,
    resetFilters,
    hasDefaultFiltersApplied,
    areObjectsEqual,
  } = usePersistedFilters<AppointmentFilters, AppointmentSort, {}>({
    viewId: 'appointmentList',
    defaultFilters: savedView?.filters ?? appointmentDefaultFilters,
    defaultSort: appointmentDefaultSort,
  });

  const applySavedView = useCallback(
    (view: AppointmentSavedView) => {
      setSelectedSavedView(view);
      setFilters(extractFiltersFromRecentSearch(view).filters);
    },
    [setFilters]
  );

  const filters = useMemo(
    () => transformSavedFiltersToApiParams({ ...persistedFilters, ...sort }),
    [persistedFilters, sort]
  );

  useEffect(() => {
    if (selectedSavedView) {
      if (
        !areObjectsEqual(
          extractFiltersFromRecentSearch(selectedSavedView).filters,
          persistedFilters
        )
      ) {
        setSelectedSavedView(null);
      }
    }
  }, [persistedFilters, selectedSavedView]);

  const { data: appointmentTypesData } = useGetAppointmentTypes();

  const {
    hasNextPage,
    data: appointmentData,
    isLoading: isLoading,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
  } = useGetAppointmentInfiniteList(filters);

  const data = useMemo(() => {
    return appointmentData?.pages.flatMap((page) => page.appointments);
  }, [appointmentData]);

  const handleEditAppointment = useCallback((appointment: Appointment) => {
    setCreateAppointmentModalOpen({
      initialValues: {},
      editAppointmentUuid: appointment.appt_uuid,
    });
  }, []);

  usePageHeader({
    title: 'Visits',
    icon: <ScheduleOutlined />,
  });

  useEffect(() => {
    analytics.page();
    RUM.setFeatureFlag('appointmentList', 'v2');
    analytics.track('Viewed Appointments List', { email: user?.email });
  }, []);

  const renderChildren = () => {
    if (viewType === 'list') {
      return (
        <Table
          data={data}
          getRowProps={(record) => {
            return {
              // className: 'cursor-pointer',
              onClick: () => {
                trackOpenedCareConnectSummary(record, user?.email, 'row');
                navigate(`/schedule/${record.appt_uuid}`);
              },
            };
          }}
          columns={getAppointmentListColumns({
            filters: filters || {},
            sort: sort || {},
            onEditAppointment: handleEditAppointment,
            userEmail: user?.email,
          })}
          loading={isLoading}
          pagination={false}
          footer={() => {
            if (!data?.length) {
              return null;
            }

            return (
              <div className="flex flex-col p-3 gap-2 items-center justify-center">
                {isFetching ? (
                  <SkeletonLoader
                    active
                    paragraph={{ rows: 1, width: 100 }}
                    title={false}
                    style={{ width: 100 }}
                  />
                ) : (
                  <div className="text-sm">
                    Showing {data?.length} of{' '}
                    {appointmentData?.pages[0]?.total || 'many'}
                  </div>
                )}
                {hasNextPage && (
                  <Button
                    loading={isFetchingNextPage}
                    onClick={() => {
                      fetchNextPage();
                    }}
                  >
                    Show more
                  </Button>
                )}
              </div>
            );
          }}
          onChange={(_, __, sorter) => {
            const sortConfig = sorter as SorterResult<Appointment>;
            updateSort({
              sortBy: sortConfig?.columnKey as string,
              sortDirection:
                sortDirectionMap[
                  sortConfig?.order as keyof typeof sortDirectionMap
                ] || undefined,
            });
          }}
        />
      );
    }

    return (
      <AppointmentListCalendarView
        filters={filters || {}}
        sort={sort}
        onEditAppointment={handleEditAppointment}
      />
    );
  };

  return (
    <>
      <div
        style={{ height: 'var(--page-wrapper-content-height)' }}
        className="page-wrapper care-connect-page overflow-hidden css-reset flex flex-col gap-2"
      >
        {!hideDashboardWidgetAccess && <AppointmentMetrics />}
        <StyledCard
          className="overflow-hidden flex max-h-full flex-col"
          styles={{
            header: {
              paddingInline: '16px',
            },
            title: {
              flexShrink: 0,
            },
            body: {
              padding: 0,
              maxHeight: '100%',
              overflow: 'auto',
              flexDirection: 'column',
            },
          }}
          title={
            <div className="flex items-center gap-3 py-2 font-normal flex-wrap">
              <AppointmentSavedViewDropdown
                screen="appointments"
                selectedViewId={selectedSavedView?.id ?? null}
                systemDefaultView={appointmentDefaultView}
                defaultViewId={defaultSavedView?.id ?? null}
                onChange={(_, value) => {
                  applySavedView(value!);
                }}
                getCurrentView={() =>
                  transformCurrentFiltersToSavedView(persistedFilters)
                }
                onDefaultChange={(_, value) => {
                  setDefaultSavedView(value || defaultSavedView);
                  if (hasDefaultFiltersApplied) {
                    applySavedView(value || defaultSavedView!);
                  }
                }}
                selectProps={{
                  className: 'w-40',
                  dropdownStyle: {
                    width: 'fit-content',
                  },
                }}
              />
              <Divider
                type="vertical"
                style={{
                  height: '20px',
                  margin: '0',
                  borderWidth: '0.5px',
                }}
              />
              <Badge dot={!hasDefaultFiltersApplied}>
                <FilterOutlined />
              </Badge>
              {!hasDefaultFiltersApplied && (
                <>
                  <span
                    className="text-sm cursor-pointer hover:text-primary hover:underline"
                    onClick={() => {
                      analytics.track('Reset Filters', {
                        email: user?.email,
                      });
                      resetFilters();
                      applySavedView(defaultSavedView!);
                    }}
                  >
                    Reset filters
                  </span>
                  <Divider
                    type="vertical"
                    style={{
                      height: '20px',
                      margin: '0',
                      borderWidth: '0.5px',
                    }}
                  />
                </>
              )}
              <PractitionerDropdown
                mode="multiple"
                value={
                  filters.practitionerIds
                    ? filters.practitionerIds?.map(Number)
                    : undefined
                }
                showSearch
                placeholder="Practitioner"
                allowClear
                onChange={(value) => {
                  analytics.track('Selected Practitioner Filter', {
                    practitioner_name: value,
                    email: user?.email,
                  });
                  updateFilters({
                    practitionerIds:
                      Array.isArray(value) && value.length > 0
                        ? value.map(Number)
                        : undefined,
                  });
                }}
              />
              {viewType === 'list' && (
                <>
                  <PatientDropdown
                    selectProps={{
                      className: 'min-w-[172px]',
                    }}
                    onChange={(patientId) => {
                      analytics.track('Entered Patient Name Search', {
                        email: user?.email,
                      });
                      updateFilters({
                        patientId: patientId ?? undefined,
                      });
                    }}
                    value={
                      filters.patientId ? Number(filters.patientId) : undefined
                    }
                  />
                  <div style={{ width: 172 }}>
                    <DateRangePicker
                      value={[
                        filters.startDate ? dayjs(filters.startDate) : null,
                        filters.endDate ? dayjs(filters.endDate) : null,
                      ]}
                      onChange={(value, __, quickFilterLabel) => {
                        const start = value?.[0] ?? null;
                        const end = value?.[1] ?? null;
                        if (quickFilterLabel) {
                          analytics.track('Entered Date Filter', {
                            date_filter: quickFilterLabel,
                            email: user?.email,
                          });
                          updateFilters({
                            startDate: quickFilterLabel,
                            endDate: quickFilterLabel,
                          });
                          return;
                        }
                        const startDate = start?.format('YYYY-MM-DD');
                        const endDate = end?.format('YYYY-MM-DD');
                        analytics.track('Applied Date Range Filter', {
                          start_date: startDate,
                          end_date: endDate,
                          email: user?.email,
                        });
                        updateFilters({
                          startDate,
                          endDate,
                        });
                      }}
                    />
                  </div>
                </>
              )}
              <AppointmentStatusDropdown
                intakeValue={
                  filters.chatCompletionStatus
                    ? [filters.chatCompletionStatus]
                    : undefined
                }
                onIntakeChange={(value) => {
                  analytics.track('Selected Status Filter', {
                    status: value.length ? `intake-${value[0]}` : 'intake-All',
                    email: user?.email,
                  });
                  updateFilters({
                    chatCompletionStatus: value.length ? value[0] : undefined,
                  });
                }}
                inVisitValue={
                  filters.inVisitStatus ? [filters.inVisitStatus] : undefined
                }
                onInVisitChange={(value) => {
                  analytics.track('Selected Status Filter', {
                    status: value.length
                      ? `invisit-${value[0]}`
                      : 'invisit-All',
                    email: user?.email,
                  });
                  updateFilters({
                    inVisitStatus: value.length ? value[0] : undefined,
                  });
                }}
                enableInVisit
              />
              <div className="ml-auto right-0 flex gap-2 items-center">
                <Segmented
                  options={[
                    {
                      label: null,
                      value: 'list',
                      icon: (
                        <Tooltip title="List view">
                          <UnorderedListOutlined title="List view" />
                        </Tooltip>
                      ),
                    },
                    {
                      label: null,
                      value: 'calendar',
                      icon: (
                        <Tooltip title="Calendar view">
                          <CalendarOutlined title="Calendar view" />
                        </Tooltip>
                      ),
                    },
                  ]}
                  value={viewType}
                  onChange={(value) => {
                    setViewType(value as 'list' | 'calendar');
                    analytics.track(
                      `Switched Appointments View to: ${(value as string).toUpperCase()}`,
                      { email: user?.email }
                    );
                  }}
                />
                {appointmentTypesData?.appointment_types?.length && (
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => {
                      analytics.track('Clicked Create New Appointment', {
                        email: user?.email,
                      });
                      const appointmentTypeId =
                        appointmentTypesData?.appointment_types?.find(
                          (type) => type.appointment_type_text === 'Intake'
                        )?.appointment_type_id ||
                        appointmentTypesData?.appointment_types?.[0]
                          ?.appointment_type_id;
                      setCreateAppointmentModalOpen({
                        initialValues: {
                          appointmentTypeId,
                        },
                      });
                    }}
                    compact={isMobile}
                    menu={{
                      trigger: ['hover'],
                      items: [
                        {
                          label: 'Bulk upload',
                          key: 'bulk-upload',
                          icon: <UploadOutlined />,
                          onClick: () => {
                            setBulkUploadModalOpen(true);
                            analytics.track(
                              'Clicked Bulk Upload Appointments',
                              {
                                email: user?.email,
                              }
                            );
                          },
                        },
                        {
                          label: 'Bulk upload history',
                          key: 'upload-history',
                          icon: <HistoryOutlined />,
                          onClick: () => {
                            setBulkUploadHistoryModalOpen(true);
                            analytics.track(
                              'Clicked Bulk Upload Appointments History',
                              {
                                email: user?.email,
                              }
                            );
                          },
                        },
                        {
                          label: 'Add Aura encounter',
                          key: 'add-aura-encounter',
                          icon: <AmbientAi />,
                          onClick: () => {
                            analytics.track(
                              'Clicked Add Aura encounter appointment',
                              {
                                email: user?.email,
                              }
                            );
                            const appointmentTypeId =
                              appointmentTypesData?.appointment_types?.find(
                                (type) =>
                                  type.appointment_type_text ===
                                  'Aura encounter'
                              )?.appointment_type_id;
                            setCreateAppointmentModalOpen({
                              initialValues: {
                                appointmentTypeId:
                                  appointmentTypeId || undefined,
                              },
                            });
                          },
                        },
                      ],
                    }}
                  >
                    Add appointment
                  </Button>
                )}
              </div>
            </div>
          }
        >
          {renderChildren()}
        </StyledCard>
      </div>
      {createAppointmentModalOpen && (
        <CreateAppointmentModal
          open={!!createAppointmentModalOpen}
          onClose={() => {
            setCreateAppointmentModalOpen(null);
          }}
          initialValues={createAppointmentModalOpen.initialValues || {}}
          editAppointmentUuid={
            createAppointmentModalOpen.editAppointmentUuid || undefined
          }
        />
      )}
      {bulkUploadModalOpen && (
        <AppointmentBulkUploadDrawer
          open={bulkUploadModalOpen}
          onClose={() => setBulkUploadModalOpen(false)}
          onViewHistory={() => setBulkUploadHistoryModalOpen(true)}
        />
      )}
      {bulkUploadHistoryModalOpen && (
        <AppointmentBulkUploadHistoryDrawer
          open={bulkUploadHistoryModalOpen}
          onClose={() => setBulkUploadHistoryModalOpen(false)}
        />
      )}
    </>
  );
};
